import { useCallback, useEffect, useMemo, useState } from "react";
import { sb } from "./db";
import { mapClient, mapInvoice, mapPayment, mapTask, mapTicket, normalizeInvoice, enrichClientRow } from "./helpers";

function mergePaginatedRows(kind, apiRows, fallbackRows, rollupData = {}) {
  if (!apiRows?.length) return apiRows || [];
  const byId = new Map((fallbackRows || []).map(r => [r.id, r]));
  const merged = apiRows.map(r => {
    const local = byId.get(r.id);
    if (kind === "clients") {
      const base = { ...r, ...(local || {}) };
      return enrichClientRow(base, rollupData.invoices, rollupData.tasks, rollupData.payments);
    }
    if (!local) return kind === "invoices" ? normalizeInvoice(r) : r;
    if (kind === "invoices") {
      const merged = { ...local, ...r, lineItems: local.lineItems?.length ? local.lineItems : r.lineItems };
      const localPaid = Number(local.paid) || 0;
      const apiPaid = Number(r.paid) || 0;
      if (apiPaid > localPaid
        || (local.status === "pending_approval" && r.status !== "pending_approval")
        || (local.status === "pending_review" && r.status !== "pending_review")) {
        merged.paid = r.paid;
        merged.pending = r.pending;
        merged.status = r.status;
      }
      return normalizeInvoice(merged);
    }
    return { ...r, ...local };
  });
  const apiIds = new Set(apiRows.map(r => r.id));
  (fallbackRows || []).filter(r => !apiIds.has(r.id)).forEach(r => {
    merged.unshift(kind === "clients"
      ? enrichClientRow(r, rollupData.invoices, rollupData.tasks, rollupData.payments)
      : r);
  });
  return kind === "invoices" ? merged.map(normalizeInvoice) : merged;
}

const TABLE_MAP = {
  clients: { table: "companies", mapper: mapClient },
  invoices: { table: "invoices", mapper: mapInvoice },
  payments: { table: "payments", mapper: mapPayment },
  tasks: { table: "tasks", mapper: mapTask },
  tickets: { table: "tickets", mapper: mapTicket },
};

export const API_PAGE_SIZE = 50;

export function buildApiFilter({ equals = {}, from, to, dateField, search, searchFields = [], order }) {
  const parts = [];
  Object.entries(equals).forEach(([field, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      parts.push(`${field}=eq.${encodeURIComponent(value)}`);
    }
  });
  if (dateField && from) parts.push(`${dateField}=gte.${encodeURIComponent(from)}`);
  if (dateField && to) parts.push(`${dateField}=lte.${encodeURIComponent(to)}`);
  if (search && searchFields.length) {
    parts.push(`q=${encodeURIComponent(search)}`);
    parts.push(`qfields=${encodeURIComponent(searchFields.join(","))}`);
  }
  if (order) parts.push(`order=${order}`);
  return parts.join("&");
}

export function useApiFilteredRows(kind, filter, fallbackRows, { enabled = true } = {}) {
  const config = TABLE_MAP[kind];
  const [rows, setRows] = useState([]);
  const [usedApi, setUsedApi] = useState(false);
  const stableFallback = useMemo(() => fallbackRows || [], [fallbackRows]);

  useEffect(() => {
    let active = true;
    if (!enabled || !config || !filter) {
      setRows([]);
      setUsedApi(false);
      return () => { active = false; };
    }

    sb.select(config.table, filter).then(rawRows => {
      if (!active) return;
      const mapped = (rawRows || []).map(config.mapper);
      setRows(mapped);
      setUsedApi(mapped.length > 0);
    }).catch(() => {
      if (!active) return;
      setRows([]);
      setUsedApi(false);
    });

    return () => { active = false; };
  }, [config, enabled, filter]);

  return { rows: usedApi ? rows : stableFallback, usedApi };
}

/** Paginated API rows with cursor-based Load more. */
export function usePaginatedApiRows(kind, filter, fallbackRows, { enabled = true, pageSize = API_PAGE_SIZE, rollupInvoices, rollupTasks, rollupPayments } = {}) {
  const config = TABLE_MAP[kind];
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [usedApi, setUsedApi] = useState(false);
  const stableFallback = useMemo(() => fallbackRows || [], [fallbackRows]);

  useEffect(() => {
    let active = true;
    if (!enabled || !config || !filter) {
      setRows([]);
      setNextCursor(null);
      setHasMore(false);
      setUsedApi(false);
      return () => { active = false; };
    }

    sb.selectPage(config.table, filter, { limit: pageSize }).then(({ items, nextCursor: cursor, hasMore: more }) => {
      if (!active) return;
      const mapped = (items || []).map(config.mapper);
      setRows(mapped);
      setNextCursor(cursor || null);
      setHasMore(!!more);
      setUsedApi(mapped.length > 0 || !!more);
    }).catch(() => {
      if (!active) return;
      setRows([]);
      setNextCursor(null);
      setHasMore(false);
      setUsedApi(false);
    });

    return () => { active = false; };
  }, [config, enabled, filter, pageSize]);

  const loadMore = useCallback(async () => {
    if (!config || !filter || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { items, nextCursor: cursor, hasMore: more } = await sb.selectPage(config.table, filter, {
        limit: pageSize,
        cursor: nextCursor,
      });
      const mapped = (items || []).map(config.mapper);
      setRows((prev) => [...prev, ...mapped]);
      setNextCursor(cursor || null);
      setHasMore(!!more);
    } catch { /* keep current rows */ }
    setLoadingMore(false);
  }, [config, filter, nextCursor, loadingMore, pageSize]);

  const displayRows = usedApi
    ? mergePaginatedRows(kind, rows, stableFallback, { invoices: rollupInvoices, tasks: rollupTasks, payments: rollupPayments })
    : (kind === "clients" && rollupInvoices?.length
      ? stableFallback.map(r => enrichClientRow(r, rollupInvoices, rollupTasks, rollupPayments))
      : stableFallback);

  return {
    rows: displayRows,
    hasMore: usedApi && hasMore,
    loadMore,
    loadingMore,
    usedApi,
  };
}
